import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** Duração padrão do toast em milissegundos. */
export const DEFAULT_TOAST_DURATION_MS = 10000;

/** Parâmetros comuns para toasts (título, mensagem e opcionais). */
export interface ToastOptions {
  title: string;
  message: string;
  duration?: number;
  icon?: string;
  variant?: 'default' | 'prototype-success';
  position?: 'top' | 'bottom';
  hideTitle?: boolean;
}

export type Notification = ToastOptions & {
  type: 'success' | 'error' | 'warning' | 'info';
};

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private notificationSubject = new BehaviorSubject<Notification | null>(null);
  private clearTimeoutId: ReturnType<typeof setTimeout> | null = null;
  public notification$ = this.notificationSubject.asObservable();

  success({ title, message, duration = DEFAULT_TOAST_DURATION_MS, icon, variant, position, hideTitle }: ToastOptions): void {
    this.show({ type: 'success', title, message, duration, icon, variant, position, hideTitle });
  }

  error({ title, message, duration = DEFAULT_TOAST_DURATION_MS, icon, variant, position, hideTitle }: ToastOptions): void {
    this.show({ type: 'error', title, message, duration, icon, variant, position, hideTitle });
  }

  warning({ title, message, duration = DEFAULT_TOAST_DURATION_MS, icon, variant, position, hideTitle }: ToastOptions): void {
    this.show({ type: 'warning', title, message, duration, icon, variant, position, hideTitle });
  }

  info({ title, message, duration = DEFAULT_TOAST_DURATION_MS, icon, variant, position, hideTitle }: ToastOptions): void {
    this.show({ type: 'info', title, message, duration, icon, variant, position, hideTitle });
  }

  private show(notification: Notification): void {
    this.cancelPendingClear();
    this.notificationSubject.next(notification);

    if (notification.duration && notification.duration > 0) {
      this.clearTimeoutId = setTimeout(() => {
        this.clear();
      }, notification.duration);
    }
  }

  clear(): void {
    this.cancelPendingClear();
    this.notificationSubject.next(null);
  }

  private cancelPendingClear(): void {
    if (this.clearTimeoutId) {
      clearTimeout(this.clearTimeoutId);
      this.clearTimeoutId = null;
    }
  }
}
